import { useMutation, useQuery } from '@apollo/client';
import AutoCompletePro from '@components/AutoComplete/AutoCompletePro';
import InputFieldControlled from '@components/InputFieldControlled';
import InputMultiSelect from '@components/InputMultiSelect';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { ADD_BRAND } from '@gql/brand';
import { GET_OPTIONS_TALENT } from '@gql/options';
import FeatureCreditsSelectField from '@modules/features/FeatureCreditsSelectField';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    FormGroup,
    FormLabel,
    Grid,
    MenuItem,
    Select,
    Stack
} from '@mui/material';
import { CreditsFeature } from '@utils/permissions';
import React, { useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

interface BrandCreateDialogFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (brandId: string) => void;
    proId?: string;
}

interface BrandCreateFormValues {
    featureId: string;
    brandName: string;
    sector: string;
    description: string;
    proId: string;
}

const BrandCreateDialogForm: React.FC<BrandCreateDialogFormProps> = ({ open, onClose, onSuccess, proId }) => {
    const { showSnackbar } = useSnackbar();

    const methods = useForm<BrandCreateFormValues>({
        mode: 'onChange',
        defaultValues: {
            proId: proId,
            featureId: '',
            brandName: '',
            sector: '',
            description: '',
        },
    });

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = methods;

    const selectedProId = watch('proId');

    const { data: optionsData } = useQuery(GET_OPTIONS_TALENT);

    const [addBrand, { loading: isCreating }] = useMutation(ADD_BRAND);

    const sectorOptions = useMemo(
        () =>
            optionsData?.options?.sectors?.[0]?.children?.map(item => ({
                label: item.name,
                value: item.id,
            })) || [],
        [optionsData],
    );

    const handleClose = () => {
        reset({
            proId,
            featureId: '',
            brandName: '',
            sector: '',
            description: '',
        });
        onClose();
    };

    const onSubmit = async (values: BrandCreateFormValues) => {
        if (!values.proId) {
            showSnackbar('Veuillez sélectionner un compte entreprise.', 'warning');
            return;
        }

        if (!values.featureId) {
            showSnackbar('Veuillez sélectionner un crédit de marque.', 'warning');
            return;
        }

        try {
            const { data } = await addBrand({
                variables: {
                    proId: values.proId,
                    brandInput: {
                        brandName: values.brandName,
                        sector: values.sector || undefined,
                        description: values.description,
                    },
                },
            });

            const createdBrandId = data?.addBrand?._id;

            if (!createdBrandId) {
                showSnackbar('La marque a été créée mais son identifiant est introuvable.', 'warning');
                handleClose();
                return;
            }

            showSnackbar('Marque créée avec succès.', 'success');
            handleClose();
            onSuccess(createdBrandId);
        } catch (err: any) {
            showSnackbar(err?.message || "Échec de la création de la marque.", 'error');
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Nouvelle marque</DialogTitle>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent>
                        <Grid container spacing={2}>
                           {!proId && <Grid item xs={12}>
                                <Controller
                                    name="proId"
                                    control={control}
                                    render={({ field }) => (
                                        <FormGroup>
                                            <FormLabel>Pour quel établissement ? </FormLabel>
                                            <AutoCompletePro
                                                onChange={(id) => {
                                                    field.onChange(id)
                                                }}
                                            />
                                        </FormGroup>
                                    )}
                                />
                            </Grid>}

                            <Grid item xs={12}>
                                <FeatureCreditsSelectField
                                    proId={selectedProId}
                                    feature={CreditsFeature.BRAND_CREDITS}
                                    requiredMessage="Veuillez sélectionner un crédit de marque."
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <InputFieldControlled
                                    name="brandName"
                                    control={control}
                                    label="Nom de la marque"
                                    placeholder="Entrez le nom de la marque"
                                    required
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12}>
                                 <InputMultiSelect
                                    single
                                    control={control}
                                    name="sector"
                                    label="Secteur d'activité"
                                    placeholder="Choisissez votre secteur"
                                    options={sectorOptions}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <InputFieldControlled
                                    name="description"
                                    control={control}
                                    label="Description"
                                    placeholder="Décrivez brièvement la marque"
                                    required
                                    multiline
                                    numberOfLines={4}
                                    fullWidth
                                    maxLength={1000}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Stack direction="row" spacing={1}>
                            <Button variant="text" color="secondary" onClick={handleClose} disabled={isCreating}>
                                Annuler
                            </Button>
                            <Button variant="contained" type="submit" disabled={isCreating}>
                                {isCreating ? 'Création...' : 'Créer la marque'}
                            </Button>
                        </Stack>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default BrandCreateDialogForm;
